// =============================================================================
// Offline cache + reconnect helpers.
//
// The app's source of truth is Supabase, but writes go through `syncData`
// which can fail when the user is offline. To support an "add stuff while
// offline → it shows up immediately → it syncs later" experience, we
// persist the in-memory data tree to localStorage on every write together
// with the last server-confirmed snapshot.
//
// On startup useData first checks the cache: if a fresh load from Supabase
// fails (offline), we boot from cache so the app is usable. When the
// browser fires `online` again, useData replays the diff from the cached
// `lastSynced` to the latest local state via the regular `syncData`
// pipeline, so no special replay code path is needed — the diff-based sync
// handles it.
//
// Keys are scoped per user-id so multiple accounts on one device don't
// collide.
// =============================================================================

const KEY_PREFIX = 'eulea_offline_v1:';

const dataKey = (userId) => `${KEY_PREFIX}${userId}:data`;
const syncedKey = (userId) => `${KEY_PREFIX}${userId}:lastSynced`;

const safeRead = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const safeWrite = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // QuotaExceeded etc. — safe to ignore, cache is best-effort.
  }
};

export const readCachedData = (userId) =>
  userId ? safeRead(dataKey(userId)) : null;

export const readCachedSynced = (userId) =>
  userId ? safeRead(syncedKey(userId)) : null;

export const writeCachedData = (userId, data) => {
  if (!userId) return;
  safeWrite(dataKey(userId), data);
};

export const writeCachedSynced = (userId, data) => {
  if (!userId) return;
  safeWrite(syncedKey(userId), data);
};

// True when the on-disk `data` snapshot differs from the last
// server-confirmed snapshot — i.e. some local edit has not been pushed yet
// (typical cause: a write failed silently on flaky wifi). Compares the raw
// JSON strings without parsing so it's cheap enough for the 5-second poll;
// both snapshots are written from the same object when in sync, so equal
// state always means equal strings.
export const hasPendingOfflineEdits = (userId) => {
  if (!userId) return false;
  try {
    const a = window.localStorage.getItem(dataKey(userId));
    const b = window.localStorage.getItem(syncedKey(userId));
    if (a === null || b === null) return false;
    return a !== b;
  } catch {
    return false;
  }
};

export const clearOfflineCache = (userId) => {
  if (!userId) return;
  try {
    window.localStorage.removeItem(dataKey(userId));
    window.localStorage.removeItem(syncedKey(userId));
  } catch {
    // ignore
  }
};

export const isOffline = () =>
  typeof navigator !== 'undefined' && navigator.onLine === false;

// Heuristic for "this looks like the network is unreachable" so we can flip
// into offline-tolerant behaviour even if navigator.onLine still claims
// true (which Safari is fond of).
export const looksLikeNetworkError = (err) => {
  if (!err) return false;
  const msg = (err.message || '').toLowerCase();
  // NOTE: deliberately NOT `err.name === 'TypeError'` — fetch network errors
  // are TypeErrors, but so is every programming bug (`x is not a function`),
  // and classifying those as "network" silently suppresses real failures.
  // All browser fetch network errors match one of the messages below
  // (Chrome "Failed to fetch", Firefox "NetworkError...", Safari "Load
  // failed" / "The Internet connection appears to be offline").
  return (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('network request failed') ||
    msg.includes('load failed') ||
    msg.includes('fetch failed') ||
    msg.includes('internet connection')
  );
};
